import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Rocket, AlertTriangle, CheckCircle2, Loader2, Clock, ExternalLink, RefreshCw } from 'lucide-react';
import { getTargetDeployStatus, rebuildTarget } from '../../lib/api/egress.js';
import type { DeployStatusDestination } from '../../lib/api/types.js';

/**
 * Build/deploy state for a `server_side` masthead.
 *
 * Replaces the git Promote panel, which asks the wrong question once a masthead
 * builds on our own compute: git branches become a recoverable snapshot rather
 * than the thing that makes a page live, so "is test ahead of main" no longer
 * tells an operator whether readers are seeing current content. This answers the
 * question they actually have — what is live, what is owed, is a build running,
 * and did the last one fail.
 */

const STATE_STYLES: Record<string, { label: string; cls: string }> = {
  claimed:    { label: 'Queued',     cls: 'bg-grey-100 text-grey-700 border-grey-200' },
  building:   { label: 'Building',   cls: 'bg-primary-50 text-primary-700 border-primary-200' },
  deploying:  { label: 'Deploying',  cls: 'bg-primary-50 text-primary-700 border-primary-200' },
  deployed:   { label: 'Live',       cls: 'bg-success-50 text-success-700 border-success-200' },
  failed:     { label: 'Failed',     cls: 'bg-danger-50 text-danger-700 border-danger-200' },
  superseded: { label: 'Superseded', cls: 'bg-grey-100 text-grey-600 border-grey-200' },
  // A rehearsal build: real work, real Cloudflare version, deliberately not live.
  shadow:     { label: 'Shadow',     cls: 'bg-warning-50 text-warning-700 border-warning-200' },
};

const IN_FLIGHT = ['claimed', 'building', 'deploying'];

function relative(ts: string | null): string {
  if (!ts) return 'never';
  const secs = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

function DestinationRow({ d, targetId }: { d: DeployStatusDestination; targetId: string }) {
  const runState = STATE_STYLES[d.latest_state || ''] || {
    label: 'No builds yet', cls: 'bg-grey-100 text-grey-600 border-grey-200',
  };
  const runInFlight = IN_FLIGHT.includes(d.latest_state || '');

  // A build can be OWED and DISPATCHED while having no run row at all.
  //
  // `startBuildRun` is called only after the build wins both the per-instance
  // memory gate and the per-masthead lease, so a build waiting on either is
  // invisible in publication_build_runs — and `latest_state` still reports the
  // PREVIOUS run. Before 2026-08-27 that meant this chip rendered a confident
  // green "Live" while a build for newer content sat in the queue behind it.
  //
  // queue_depth counts pending/running/retrying build_deploy jobs for this pair,
  // so it sees exactly what the run rows cannot. With the publishing service now
  // running a single instance (max_instances: 1), waiting is the NORMAL case
  // rather than a rarity, which is what makes this worth rendering.
  const queued = (d.queue_depth ?? 0) > 0;
  const style = queued && !runInFlight
    ? { label: 'Queued', cls: 'bg-grey-100 text-grey-700 border-grey-200' }
    : runState;
  const inFlight = runInFlight || (queued && !runInFlight);
  const isMain = d.destination_type === 'main_branch';

  const queryClient = useQueryClient();
  const [note, setNote] = useState<string | null>(null);

  const rebuild = useMutation({
    mutationFn: () =>
      rebuildTarget(targetId, d.destination_type as 'test_branch' | 'main_branch'),
    onSuccess: (res) => {
      // 'coalesced' is a success, not a rejection — a build was already owed and
      // this click joined it. Saying so stops an operator clicking again.
      setNote(
        res.outcome === 'coalesced'
          ? 'A build was already owed — this joined it.'
          : 'Build queued.'
      );
      queryClient.invalidateQueries({ queryKey: ['deploy-status', targetId] });
    },
    onError: (err: any) => {
      setNote(err?.response?.data?.message || err?.message || 'Rebuild failed.');
    },
  });

  // Production ships to readers, so it asks first. Preview carries no traffic.
  const onRebuild = () => {
    setNote(null);
    if (isMain && !window.confirm(
      'Rebuild Production?\n\nThis rebuilds from the current head of main and ' +
      'deploys the result to readers. Merge your change first — the build picks ' +
      'up whatever is on main when it runs.'
    )) return;
    rebuild.mutate();
  };

  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-grey-100 last:border-0">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-grey-900">{isMain ? 'Production' : 'Preview'}</span>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${style.cls}`}>
            {inFlight && <Loader2 className="h-3 w-3 animate-spin" />}
            {style.label === 'Failed' && <AlertTriangle className="h-3 w-3" />}
            {style.label === 'Live' && <CheckCircle2 className="h-3 w-3" />}
            {style.label}
          </span>
          {d.latest_lane === 'fast' && (
            <span className="text-xs text-grey-500" title="Price-sensitive or coverage-customer content builds immediately">
              fast lane
            </span>
          )}
        </div>

        <p className="mt-1 text-sm text-grey-600">
          {d.behind_record
            ? 'Content has moved on since the last deploy — a build is owed.'
            : queued
              // Not "current": the clock can be level while a dispatched build
              // for this pair is still waiting its turn.
              ? 'A build is queued for this destination.'
              : d.live_content_version !== null
                ? 'Live content is current.'
                : 'Nothing deployed from our compute yet.'}
          {' '}
          <span className="text-grey-500">
            live v{d.live_content_version ?? '—'} · record v{d.record_content_version ?? '—'}
          </span>
        </p>

        {d.latest_error && (
          <p className="mt-1 text-sm text-danger-700 break-words">{d.latest_error}</p>
        )}

        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-grey-500">
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {d.deployed_at ? `deployed ${relative(d.deployed_at)}` : `last run ${relative(d.latest_started_at)}`}
          </span>
          {d.cf_version_id && (
            <span className="tabular-nums" title={d.cf_version_id}>
              {/* test uploads carry an alias and no traffic; main carries a deployment */}
              {d.cf_kind === 'deployment' ? 'deployment' : `alias ${d.cf_preview_alias || '—'}`}
              {' '}{d.cf_version_id.slice(0, 8)}
            </span>
          )}
          {d.commit_sha && <span className="tabular-nums">commit {d.commit_sha.slice(0, 7)}</span>}
          {/* Was `> 1`, which rendered nothing for the single queued build — the
              common case now that one instance builds at a time. */}
          {queued && (
            <span
              className="text-warning-700"
              title={
                d.queue_depth > 1
                  ? 'Publishes are arriving faster than builds complete; they coalesce into one build.'
                  : 'A build is queued for this destination and has not started yet.'
              }
            >
              {d.queue_depth === 1 ? 'build queued' : `${d.queue_depth} builds queued`}
            </span>
          )}
        </div>

        {note && <p className="mt-1 text-xs text-grey-600">{note}</p>}
      </div>

      <div className="shrink-0">
        <button
          type="button"
          onClick={onRebuild}
          disabled={rebuild.isPending}
          title={
            'Rebuild and deploy this destination without publishing anything. ' +
            'Use after merging a template, layout, robots.txt, sitemap or CSS ' +
            'change — the publish path never sees those.'
          }
          className="inline-flex items-center gap-1.5 rounded-lg border border-grey-300 bg-white px-3 py-1.5 text-sm font-medium text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {rebuild.isPending
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <RefreshCw className="h-3.5 w-3.5" />}
          Rebuild
        </button>
      </div>
    </div>
  );
}

export function DeployStatusPanel({ targetId, publishDomain }: { targetId: string; publishDomain?: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['deploy-status', targetId],
    queryFn: () => getTargetDeployStatus(targetId),
    // A build is ~2-3 minutes end to end, so a slow poll keeps an in-flight
    // build visibly progressing without hammering the endpoint.
    refetchInterval: 15_000,
  });

  if (isLoading) {
    return (
      <div className="rounded-xl border border-grey-200 bg-grey-50 p-6 shadow-md">
        <div className="flex items-center gap-2 text-grey-600">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Loading deploy status…</span>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-xl border border-danger-200 bg-danger-50 p-6 shadow-md">
        <div className="flex items-center gap-2 text-danger-700">
          <AlertTriangle className="h-5 w-5" />
          <span className="text-sm font-medium">Deploy status unavailable</span>
        </div>
      </div>
    );
  }

  const anyFailed = data.destinations.some(d => d.latest_state === 'failed');
  const anyBuilding = data.destinations.some(d => IN_FLIGHT.includes(d.latest_state || ''));

  return (
    <div className={`rounded-xl border p-6 shadow-md ${
      anyFailed ? 'bg-danger-50 border-danger-200'
        : anyBuilding ? 'bg-primary-50 border-primary-200'
        : 'bg-surface border-grey-200'
    }`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <Rocket className={`h-6 w-6 ${anyFailed ? 'text-danger-600' : anyBuilding ? 'text-primary-600' : 'text-grey-400'}`} />
          <div>
            <h2 className="text-lg font-bold text-grey-900">
              {anyFailed ? 'Build failed' : anyBuilding ? 'Building' : 'Server-side deploys'}
            </h2>
            <p className="text-sm text-grey-600">
              This masthead builds on our own compute and deploys straight to Cloudflare.
              Git remains a recoverable snapshot.
            </p>
          </div>
        </div>
        {publishDomain && (
          <a
            href={`https://${publishDomain}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-800 font-medium"
          >
            <ExternalLink className="h-3 w-3" />
            Visit site
          </a>
        )}
      </div>

      <div className="mt-2">
        {data.destinations.length === 0
          ? <p className="text-sm text-grey-500 py-3">No destinations configured.</p>
          : data.destinations.map(d => <DestinationRow key={d.destination_type} d={d} targetId={targetId} />)}
      </div>
    </div>
  );
}
